<template>
  <v-container class="settings-container">
    <!-- Page Header -->
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col :cols="$vuetify.display.smAndDown ? 12 : 'auto'">
        <h1 class="page-title mb-1">{{ isArchived ? 'Users Up for Deletion' : 'Users' }}</h1>
        <p class="body-text text-medium-emphasis mt-2">
          {{ isArchived ? 'Manage user accounts scheduled for deletion.' :
          'View and manage all user accounts in your system.'
          }}
        </p>
      </v-col>
      <v-col :cols="$vuetify.display.smAndDown ? 12 : 'auto'" class="text-right">
        <v-breadcrumbs :items="breadcrumbItems" class="pa-0">
          <template v-slot:divider>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-breadcrumbs>
      </v-col>
    </v-row>

    <!-- Search and Action Buttons -->
    <div :class="[
      'mb-6', $vuetify.display.smAndDown ? 'd-flex flex-column ga-3' : 'd-flex align-center ga-2']">
      <div
        :class="$vuetify.display.smAndDown ? 'w-100' : (isArchived ? '' : 'flex-grow-1')"
        :style="isArchived ? 'flex: 0 0 560px; width: 560px; max-width: 100%;' : 'min-width: 0;'"
      >
        <v-text-field v-model="pagination.search" density="compact" label="Search by name or email" clearable
          prepend-inner-icon="mdi-magnify" variant="outlined" hide-details />
      </div>
      <div v-if="!isArchived" :class="$vuetify.display.smAndDown ? 'w-100' : ''" style="width: 240px; max-width: 100%;">
        <v-select v-model="pagination.role" :items="roleFilterOptions" item-title="title" item-value="value"
          density="compact" label="Filter by role" variant="outlined" clearable hide-details chips />
      </div>
      <div v-if="!isArchived" :class="$vuetify.display.smAndDown ? 'w-100' : ''" style="width: 240px; max-width: 100%;">
        <v-select v-model="pagination.status" :items="statusFilterOptions" item-title="title" item-value="value"
          density="compact" label="Filter by status" variant="outlined" clearable hide-details  chips/>
      </div>
      <div v-if="!isArchived" :class="$vuetify.display.smAndDown ? 'w-100' : 'ms-auto'">
        <v-btn prepend-icon="mdi-delete-clock" color="error" variant="outlined" :block="$vuetify.display.smAndDown"
          @click="$router.push({ name: 'users.archive.list' })">
          View Deletion Queue
        </v-btn>
      </div>
    </div>

    <!-- Dynamic Component Rendering | User List & User Archive List -->
    <component :is="currentComponent" :pagination="pagination" :headers="headers" :list-data="listData"
      :user-roles-method="getUserRoles" :subscription-methods="subscriptionMethods" @load-items="loadItems"
      @update-pagination="updatePagination" />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import helperMixin from '@/mixins/helper'
import userMixin from '@/mixins/user'
import ActiveUser from '@/components/user/ActiveUser.vue'
import ArchivedUser from '@/components/user/ArchivedUser.vue'
import { resourceTypes } from '@/shared/constants/generalList/list'
import roleTypes from '@/store/roles'

export default {
  components: {
    ActiveUser,
    ArchivedUser
  },
  mixins: [helperMixin, userMixin],
  data() {
    return {
      pagination: {
        search: '',
        role: null,
        status: null,
        currentPage: 1,
        itemsPerPage: 15,
        perPageOptions: [15, 25, 50, 100],
        totalItems: 0
      },
      listData: {
        users: [],
        loading: false,
        error: false,
        message: null
      }
    }
  },
  computed: {
    ...mapGetters(['isAdmin', 'isSuperCustomer']),

    isArchived() {
      return this.$route.name === 'users.archive.list'
    },

    currentComponent() {
      return this.isArchived ? 'ArchivedUser' : 'ActiveUser'
    },

    breadcrumbItems() {
      if (this.isArchived) {
        return [
          {
            title: 'Dashboard',
            disabled: false,
            href: '/'
          },
          {
            title: 'Users',
            to: { name: 'users.index' },
            disabled: false
          },
          {
            title: 'Deletion Queue',
            disabled: true
          }
        ]
      }
      return [
        {
          title: 'Dashboard',
          disabled: false,
          href: '/'
        },
        {
          title: 'Users',
          disabled: true
        }
      ]
    },

    roleFilterOptions() {
      return [
        { title: 'Admin', value: roleTypes.ADMIN },
        { title: 'Customer', value: roleTypes.CUSTOMER },
        { title: 'Super Customer', value: roleTypes.SUPER_CUSTOMER }
      ]
    },

    statusFilterOptions() {
      return [
        { title: 'Active', value: 'active' },
        { title: 'Inactive', value: 'inactive' }
      ]
    },

    headers() {
      const baseHeaders = [
        { title: '#', key: 'serial_no', sortable: false, align: 'center' },
        { title: 'Name', key: 'name', sortable: true, width: '12%' },
        { title: 'Email', key: 'email', sortable: true, width: '17%' },
        ...(this.isArchived ? [] : [{ title: 'Subscription', key: 'subscription', sortable: false }]),
        { title: 'Role', key: 'role', sortable: true },
        { title: 'Forms', key: 'forms_count', sortable: true, align: 'center' },
        { title: 'Total Leads', key: 'user_leads_count', sortable: true, align: 'center' },
      ];

      const conditionalHeaders = this.isArchived
        ? [
          { title: 'Deletion Date', key: 'delete_at', sortable: true },
          { title: 'Reason', key: 'delete_source', sortable: true, align: 'center' },
          { title: 'Actions', key: 'actions', sortable: false, align: 'center' }
        ]
        : [
          { title: 'Completed', key: 'user_completed_leads_count', sortable: true, align: 'center' },
          { title: 'Partial', key: 'user_partial_leads_count', sortable: true, align: 'center' },
          { title: 'Created At', key: 'created_at', sortable: true }
        ];

      return [...baseHeaders, ...conditionalHeaders];
    },

    // Subscription methods object to pass to child components
    subscriptionMethods() {
      return {
        getTitle: this.getSubscriptionTitle,
        getStatus: this.getSubscriptionStatus,
        getUserSubscriptionStatus: this.getUserSubscriptionStatus
      }
    }
  },
  methods: {
    loadItems({ page, itemsPerPage, sortBy }) {
      this.setLoading(true)
      if (page) this.pagination.currentPage = page
      if (itemsPerPage) this.pagination.itemsPerPage = itemsPerPage

      const params = this.buildRequestParams({ page, sortBy })
      this.fetchUsers(params)
    },

    buildRequestParams({ page, sortBy }) {
      return {
        page: page || this.pagination.currentPage,
        per_page: this.pagination.itemsPerPage,
        search: {
          name: this.pagination.search || '',
          email: this.pagination.search || ''
        },
        filters: {
          role: this.pagination.role || '',
          status: this.isArchived ? 'deleted' : (this.pagination.status || '')
        },
        sortField: sortBy?.length ? this.mapSortField(sortBy[0].key) : 'id',
        sortDirection: sortBy?.length ? sortBy[0].order : 'desc',
        archived: this.isArchived,
        resource: resourceTypes.USERS_LIST
      }
    },

    mapSortField(key) {
      const sortFieldMap = {
        'name': 'name',
        'email': 'email',
        'forms_count': 'forms_count',
        'user_leads_count': 'user_leads_count',
        'created_at': 'created_at'
      }
      return sortFieldMap[key] || key
    },

    async fetchUsers(params) {
      if (!this.isAdmin) {
        this.handleError({ message: 'Access denied. Admin privileges required.' })
        return
      }

      try {
        const response = await this.$store.dispatch('userlists/fetchUserLists', params)
        this.updateUsersData(response?.data)
        if (!this.isArchived) {
          await this.$store.dispatch('userlists/fetchUserListsSuccess', response?.data?.data || [])
        }
      } catch (err) {
        this.handleError(err)
        if (!this.isArchived) {
          await this.$store.dispatch('userlists/fetchUserListsSuccess', [])
        }
      } finally {
        this.setLoading(false)
      }
    },

    updateUsersData(data) {
      this.listData.users = (data?.data || []).map(user => ({
        ...user,
        speedDial: this.isArchived ? false : undefined
      }))
      this.pagination.totalItems = data?.pagination?.total || 0
      this.listData.error = false
      this.listData.message = null
    },

    setLoading(status) {
      this.listData.loading = status
    },

    handleError(err) {
      this.listData.error = true
      this.listData.message = err?.response?.data?.message || err?.message || 'An error occurred while loading users'
    },

    updatePagination(newPagination) {
      this.pagination = { ...this.pagination, ...newPagination }
    },

    // Shared user roles method
    getUserRoles(roles) {
      let rolesArray = []
      for (let role of roles) {
        rolesArray.push(role.title)
      }
      return rolesArray.join('<br>')
    },

    // Shared subscription methods
    getSubscriptionTitle(user) {
      let data = this.getUserSubscriptionStatus(user)
      if (data && data.includes('(')) {
        return data.split('(')[0]
      }
      return data
    },

    getSubscriptionStatus(user) {
      let data = this.getUserSubscriptionStatus(user)
      let newData = ''
      if (data && data.includes('(')) {
        newData = '( ' + data.split('(')[1]
      }
      return newData
    },

    getUserSubscriptionStatus(user) {
      if (user.one_tool_user) {
        return `${user.plan.title} ${(user.one_tool_user.in_trial ? '( Trial )' : '')}`
      }
      if (this.hasActiveSubscriptionByUser(user)) {
        let subscriptionStatus = ''
        if (user.plan.subscription.stripe) {
          subscriptionStatus = `( ${user.plan.subscription.stripe_status} )`.toUpperCase()
        } else {
          subscriptionStatus = `( ${user.plan.subscription.status} )`.toUpperCase()
        }
        return `${user.plan.title} ${subscriptionStatus}`
      }
      return 'Free'
    }
  },
  watch: {
    'pagination.search': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    'pagination.role': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    'pagination.status': {
      handler() {
        this.pagination.currentPage = 1
        this.loadItems({})
      }
    },

    '$route'() {
      // Reset data when route changes
      this.listData.users = []
      this.pagination = {
        ...this.pagination,
        search: '',
        role: null,
        status: null,
        currentPage: 1
      }
      this.loadItems({})
    }
  },
  mounted() {
    this.loadItems({})
  }
}
</script>
